import React, { useEffect, useState, useRef } from 'react';
import { AlertTriangle, Calendar as CalendarIcon, X } from 'lucide-react';
import { Calendar } from '../../BulkActionsManager/components/Calendar/Calendar';
interface DateTimePickerProps {
  value: string; // Date string in format "YYYY-MM-DD" or "YYYY-MM-DD HH:MM"
  onChange: (value: string) => void;
  onClose: () => void;
  // Optional scheduled dates to highlight, with a legend label
  highlightedDates?: Date[];
  highlightLegend?: string;
  // Returns a warning to show for the selected date, or null
  getWarning?: (date: Date) => string | null;
  'data-id'?: string;
}
export function DateTimePicker({
  value,
  onChange,
  onClose,
  highlightedDates,
  highlightLegend,
  getWarning,
  'data-id': dataId
}: DateTimePickerProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // Parse the value string to Date object
  const parseValue = (val: string): Date => {
    if (!val) {
      // Default to today at 9:00 AM
      const today = new Date();
      today.setHours(9, 0, 0, 0);
      return today;
    }
    // Handle "YYYY-MM-DD" or "YYYY-MM-DD HH:MM" format
    const [datePart, timePart] = val.split(' ');
    if (!datePart) {
      const today = new Date();
      today.setHours(9, 0, 0, 0);
      return today;
    }
    const [year, month, day] = datePart.split('-').map(Number);
    // Validate date parts
    if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
      const today = new Date();
      today.setHours(9, 0, 0, 0);
      return today;
    }
    const date = new Date(year, month - 1, day);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      const today = new Date();
      today.setHours(9, 0, 0, 0);
      return today;
    }
    if (timePart && timePart.includes(':')) {
      const [hours, minutes] = timePart.split(':').map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        date.setHours(hours, minutes, 0, 0);
      } else {
        // Default to 9:00 AM if time is invalid
        date.setHours(9, 0, 0, 0);
      }
    } else {
      // Default to 9:00 AM if no time specified
      date.setHours(9, 0, 0, 0);
    }
    return date;
  };
  const [selectedDate, setSelectedDate] = useState<Date>(parseValue(value));
  const warning = getWarning ? getWarning(selectedDate) : null;
  // Format Date object back to string
  const formatValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };
  const handleDateTimeChange = (newDate: Date) => {
    setSelectedDate(newDate);
    onChange(formatValue(newDate));
  };
  const handleConfirm = () => {
    onChange(formatValue(selectedDate));
    onClose();
  };
  const handleCancel = () => {
    onClose();
  };
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-[rgba(15,23,42,0.5)]" data-id="element-4874" data-mp="244p8fgq3jubl">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="datetime-picker-title" data-id="element-4875" data-mp="1geusuree7rnn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]" data-id="element-4876" data-mp="eomszptn1c68">
          <div className="flex items-center gap-3" data-id="element-4877" data-mp="iskto4ym2v1e">
            <CalendarIcon className="w-5 h-5 text-[#475569]" data-id="element-4878" data-mp="nqw1yb99cxhi" />
            <h2 id="datetime-picker-title" className="text-[20px] font-semibold text-[#0F172A]" data-id="element-4879" data-mp="npkzcerjtvrz">
              Select Date & Time
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F1F5F9] transition-colors" aria-label="Close" data-id="element-4880" data-mp="xgfl7i0vutcw">
            <X className="w-5 h-5 text-[#64748B]" data-id="element-4881" data-mp="12eh7ebtlv8sl" />
          </button>
        </div>

        {/* Calendar with Time Picker */}
        <div className="p-6 flex justify-center" data-id="element-4882" data-mp="1bh04efapmr6x">
          <Calendar value={selectedDate} onChange={handleDateTimeChange} includeTime={true} highlightedDates={highlightedDates} className="shadow-none" data-id="element-4883" data-mp="1bfp4wroas5lm" />
        </div>

        {(highlightLegend || warning) && <div className="px-6 pb-4 space-y-3" data-id="element-4884" data-mp="3d94wprw4fqi6">
            {highlightLegend && <p className="flex items-center gap-2 text-[12px] text-[#475569]" data-id="element-4885" data-mp="35pca2v8wtrlf">
                <span className="w-3 h-3 rounded-sm bg-[#E0F2FE] border border-[#BAE6FD]" aria-hidden="true" data-id="element-4886" data-mp="30ydujxvqwxj8" />
                {highlightLegend}
              </p>}
            {warning && <div className="flex items-start gap-2 p-3 rounded-lg border border-[#FDE68A] bg-[#FFFBEB]" role="alert" data-id="element-4887" data-mp="2vwyaa81e6v1w">
                <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-4888" data-mp="3onokslfgz9j4" />
                <p className="text-[12px] text-[#92400E]" data-id="element-4889" data-mp="3olc7wmokjpcx">{warning}</p>
              </div>}
          </div>}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E2E8F0]" data-id="element-4890" data-mp="3eoze7ot0xbi6">
          <button onClick={handleCancel} className="h-10 px-5 bg-white border border-[#CBD5E1] rounded-md text-[#0F172A] text-[14px] font-medium hover:bg-[#F1F5F9] hover:border-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all duration-200" data-id="element-4891" data-mp="3ao72mowoiuiq">
            Cancel
          </button>
          <button onClick={handleConfirm} className="h-10 px-5 bg-[#0EA5E9] rounded-md text-white text-[14px] font-medium hover:bg-[#0284C7] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all duration-200" data-id="element-4892" data-mp="20r8r77p21l13">
            Confirm
          </button>
        </div>
      </div>
    </div>;
}